import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Chip,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import api from '../../services/api';
import { useDateFormat } from '../../hooks/useDateFormat';
import {
  timesheetEntryDateKey,
  timesheetEntryHoursLabel,
  timesheetEntryUserLabel
} from '../../utils/timesheetEntryDisplay';
import { useAccountDashboardOptional } from './AccountDashboardContext';

interface TimesheetEntry {
  id: string;
  taskId: string;
  startTime?: string;
  stopTime?: string;
  hours?: number;
  task?: {
    name?: string;
    taskNumber?: string;
  };
  user?: {
    displayName?: string;
    email?: string;
    contacts?: Array<{ contactValue?: string; isPrimary?: boolean; contactType?: string }>;
  };
}

interface AccountTimesheetSectionProps {
  accountId: string;
  organizationId: string;
}

export default function AccountTimesheetSection({
  accountId,
  organizationId
}: AccountTimesheetSectionProps) {
  const dashboard = useAccountDashboardOptional();
  const { formatDateOnly, formatTimeOnly } = useDateFormat();
  const [entries, setEntries] = useState<TimesheetEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [taskIds, setTaskIds] = useState<string[]>([]);

  const focusWindow = dashboard?.focusWindow;
  const accountTimezone = dashboard?.accountTimezone ?? 'UTC';
  const windowLabel = focusWindow?.label ?? 'Last 30 days';

  const loadEntries = useCallback(async () => {
    try {
      setLoading(true);
      const tasksResponse = await api.getTasks({ accountId });
      const accountTaskIds = (tasksResponse.data || []).map((t: { id: string }) => t.id);
      setTaskIds(accountTaskIds);

      if (accountTaskIds.length === 0) {
        setEntries([]);
        return;
      }

      let startDate: string;
      let endDate: string;
      if (focusWindow) {
        startDate = focusWindow.from;
        endDate = focusWindow.to;
      } else {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 30);
        startDate = start.toISOString().split('T')[0];
        endDate = end.toISOString().split('T')[0];
      }

      const response = await api.getTimesheetEntries({
        organizationId,
        startDate,
        endDate,
        timezone: accountTimezone
      });

      const filtered = (response.data || []).filter((entry: TimesheetEntry) =>
        accountTaskIds.includes(entry.taskId)
      );
      setEntries(filtered);
    } catch (err) {
      console.error('Error loading timesheet entries:', err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [accountId, organizationId, focusWindow, accountTimezone]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Timesheet entries
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {focusWindow ? `Entries: ${windowLabel}` : windowLabel}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {loading ? (
          <LinearProgress />
        ) : entries.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Task</TableCell>
                  <TableCell>Hours</TableCell>
                  <TableCell>User</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.slice(0, 20).map((entry) => {
                  const dateKey = timesheetEntryDateKey(entry);
                  return (
                    <TableRow key={entry.id}>
                      <TableCell>{dateKey ? formatDateOnly(dateKey) : 'N/A'}</TableCell>
                      <TableCell>
                        {entry.startTime && entry.stopTime ? (
                          <>
                            {formatTimeOnly(entry.startTime)} - {formatTimeOnly(entry.stopTime)}
                          </>
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      <TableCell>
                        {entry.task?.name || 'N/A'}
                        {entry.task?.taskNumber && (
                          <Chip label={entry.task.taskNumber} size="small" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell>{timesheetEntryHoursLabel(entry)}</TableCell>
                      <TableCell>{timesheetEntryUserLabel(entry)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No timesheet entries found{taskIds.length === 0 ? ' (no tasks on account)' : ''}.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
